import { useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import { motion } from 'motion/react';
import {
  Archive, ChevronRight, ChevronsLeft, ChevronsRight, File, Flag, FolderInput, FolderPlus, FolderTree, Inbox, Mail, PenSquare, Send, Settings,
  ShieldAlert, Sparkles, Sun, Trash2, Users, Workflow,
} from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useUi, type View } from '@/lib/store';
import { qk, UNREAD_EXCLUDED, useFolders, useMailActions, useMe } from '@/lib/queries';
import { composeNew, composeWithAi } from '@/lib/compose';
import { useCrm } from '@/lib/crm';
import { api, errorMessage } from '@/lib/api';
import { Avatar, Favicon } from './Avatar';
import dtsMark from '@/dts-mark.svg';
import { askText, confirmDialog } from './Dialogs';
import { domainOf, isFreemail, type FolderCount } from '@dts/core';
import {
  DeleteFolderDialog, FloatingMenu, folderMenuItems, FolderIcon, folderLeaf, folderParent, isSelfOrInside, MergeFolderDialog, moveFolder,
  MoveFolderDialog, renameFolder, type MenuAction,
} from './FolderActions';
import { PoweredBy } from './PoweredBy';
import { cn, IconButton, Num } from './ui';

const ICONS: Record<string, ReactNode> = {
  Inbox: <Inbox />, Sent: <Send />, Drafts: <File />, Archive: <Archive />, Junk: <ShieldAlert />, Trash: <Trash2 />,
};

function sameView(a: View, b: View): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'mail' && b.kind === 'mail') return a.folder === b.folder && a.recipient === b.recipient && a.domain === b.domain && (a.filter ?? 'all') === (b.filter ?? 'all') && !a.search;
  return true;
}

const FOLDER_DRAG = 'application/x-dts-folder';

function NavItem({ icon, label, count, target, collapsed, onDropIds, accent, folderDrag, onDropFolder, onContextMenu, depth = 0, expanded, onToggle, title }: {
  icon: ReactNode; label: string; count?: number; target: View; collapsed: boolean; onDropIds?: (ids: string[]) => void; accent?: boolean;
  /** Custom folders can be dragged onto another folder to merge them. */
  folderDrag?: string; onDropFolder?: (name: string, at: { x: number; y: number }) => void; onContextMenu?: (e: MouseEvent) => void;
  /** Subfolder nesting level, and for a folder with subfolders whether they're shown. */
  depth?: number; expanded?: boolean; onToggle?: () => void; title?: string;
}) {
  const view = useUi((s) => s.view);
  const setView = useUi((s) => s.setView);
  const active = sameView(view, target);
  const [over, setOver] = useState(false);
  return (
    <button
      onClick={() => setView(target)}
      title={collapsed ? (title ?? label) : title}
      onContextMenu={onContextMenu}
      draggable={!!folderDrag}
      onDragStart={folderDrag ? (e) => e.dataTransfer.setData(FOLDER_DRAG, folderDrag) : undefined}
      onDragOver={(e) => {
        const types = e.dataTransfer.types;
        if (!(onDropIds && types.includes('application/x-dts-ids')) && !(onDropFolder && types.includes(FOLDER_DRAG))) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        setOver(false);
        const folder = e.dataTransfer.getData(FOLDER_DRAG);
        if (folder) {
          if (folder !== folderDrag) onDropFolder?.(folder, { x: e.clientX, y: e.clientY });
          return;
        }
        const ids = e.dataTransfer.getData('application/x-dts-ids');
        if (ids) onDropIds?.(JSON.parse(ids));
      }}
      className={cn(
        'nav-item group relative flex h-9 w-full items-center gap-3 rounded-xl px-2.5 text-[13.5px] transition-colors',
        active ? 'text-fg' : 'text-muted hover:text-fg',
        over && 'bg-accent/15 ring-1 ring-accent/50',
        collapsed && 'justify-center px-0',
      )}
      style={!collapsed && depth ? { paddingLeft: 10 + depth * 16 } : undefined}
    >
      {active && <motion.span layoutId="nav-pill" className="nav-pill absolute inset-0 rounded-xl bg-line-strong/80" transition={{ type: 'spring', stiffness: 500, damping: 38 }} />}
      {!collapsed && onToggle && (
        <span
          role="button"
          aria-label={expanded ? 'Hide subfolders' : 'Show subfolders'}
          onClick={(e) => (e.stopPropagation(), onToggle())}
          className="relative z-10 -ml-1.5 -mr-2 flex size-4 items-center justify-center rounded text-faint hover:text-fg"
        >
          <ChevronRight className={cn('size-3.5 transition-transform', expanded && 'rotate-90')} />
        </span>
      )}
      <span className={cn('relative z-10 [&_svg]:size-[17px]', active && (accent ? 'text-accent' : 'text-fg'))}>{icon}</span>
      {!collapsed && <span className="relative z-10 flex-1 truncate text-left font-medium">{label}</span>}
      {!!count && count > 0 && (
        <span className={cn('pill relative z-10 rounded-full text-[11px] font-semibold tabular-nums', collapsed ? 'absolute right-1 top-1 size-2 bg-accent text-transparent' : 'px-1.5 py-px', !collapsed && (active ? 'bg-accent text-accent-fg' : 'bg-line text-fg'))}>
          {collapsed ? '' : <Num>{count > 999 ? '999+' : count}</Num>}
        </span>
      )}
    </button>
  );
}

export function Sidebar() {
  const collapsed = useUi((s) => s.local.sidebarCollapsed);
  const setLocal = useUi((s) => s.setLocal);
  const collapsedFolders = useUi((s) => s.local.collapsedFolders);
  const folders = useFolders().data ?? [];
  const me = useMe().data;
  const actions = useMailActions();
  const qc = useQueryClient();
  const crmRunning = useCrm((s) => s.runs.some((r) => r.status === 'running'));
  const setCrmPanel = useCrm((s) => s.setPanel);
  const isMac = false; // no window traffic lights in the browser
  const inboxUnread = folders.find((f) => f.name === 'Inbox')?.unread ?? 0;
  const draftCount = folders.find((f) => f.name === 'Drafts')?.total ?? 0;
  const unreadEverywhere = folders.filter((f) => !UNREAD_EXCLUDED.includes(f.name)).reduce((n, f) => n + f.unread, 0);
  const custom = folders.filter((f) => !f.isSystem);
  const identities = me?.addresses.filter((a) => a.enabled) ?? [];
  const domains = Array.from(new Set(identities.map((a) => a.domain)));

  const move = (folder: string) => (ids: string[]) => {
    void actions.run(ids, folder === 'Trash' ? 'trash' : folder === 'Archive' ? 'archive' : folder === 'Junk' ? 'junk' : 'move', folder);
    toast.success(`Moved ${ids.length} to ${folder}`);
  };

  const refreshMail = () => {
    qc.invalidateQueries({ queryKey: qk.folders });
    qc.invalidateQueries({ queryKey: ['messages'] });
  };

  /** With a parent, the new folder is created inside it. */
  const newFolder = async (parent?: string) => {
    const name = await askText(parent ? `New folder in ${folderLeaf(parent)}` : 'New folder', {
      placeholder: parent ? 'Folder name, or a domain like apple.com' : 'Folder name, a domain like apple.com, or Parent/Child',
      confirmLabel: 'Create',
    });
    if (!name?.trim()) return;
    try {
      const r = await api.createFolder(parent ? `${parent}/${name.trim()}` : name.trim());
      if (parent) setLocal({ collapsedFolders: collapsedFolders.filter((f) => f !== parent) });
      refreshMail();
      if (r.domainRule) toast.success(`Mail from ${r.name} now goes to its folder`, { description: r.moved ? `Moved ${r.moved} existing email${r.moved === 1 ? '' : 's'}.` : undefined });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  /** Dropping an email on the Folders header: folder named after the sender's domain, with all its mail moved in. */
  const fileDomain = async (from: string) => {
    const domain = domainOf(from);
    if (!domain) return;
    if (isFreemail(domain) && !(await confirmDialog(`Make a folder for all ${domain} mail?`,
      `${domain} is shared by millions of people, so every email from any ${domain} address would go there.`, { confirmLabel: 'Create folder' }))) return;
    const t = toast.loading(`Filing mail from ${domain}…`);
    try {
      const r = await api.fileDomain(from);
      refreshMail();
      toast.success(`${r.created ? 'Created' : 'Updated'} ${r.name}`, { id: t, description: `Moved ${r.moved} email${r.moved === 1 ? '' : 's'}. New mail from ${r.name} will go there too.` });
    } catch (e) {
      toast.error(errorMessage(e), { id: t });
    }
  };
  const [overFolders, setOverFolders] = useState(false);

  const view = useUi((s) => s.view);
  const setView = useUi((s) => s.setView);
  const [menu, setMenu] = useState<{ items: MenuAction[]; x: number; y: number } | null>(null);
  const [moving, setMoving] = useState<FolderCount | null>(null);
  const [merging, setMerging] = useState<{ folder: FolderCount; target?: string } | null>(null);
  const [deleting, setDeleting] = useState<FolderCount | null>(null);
  const folderByName = (name: string) => folders.find((f) => f.name === name);

  // Subfolders ("Shopping/apple.com") nest under their parent, in name order; a collapsed parent hides them.
  const names = new Set(custom.map((f) => f.name));
  const parentOf = (name: string) => {
    const p = folderParent(name);
    return p && names.has(p) ? p : null;
  };
  const tree: { folder: FolderCount; depth: number; hasChildren: boolean; unreadInside: number }[] = [];
  const addLevel = (parent: string | null, depth: number) => {
    for (const f of custom.filter((c) => parentOf(c.name) === parent).sort((a, b) => folderLeaf(a.name).localeCompare(folderLeaf(b.name)))) {
      const inside = custom.filter((c) => c.name !== f.name && isSelfOrInside(c.name, f.name));
      tree.push({ folder: f, depth, hasChildren: inside.length > 0, unreadInside: inside.reduce((n, c) => n + c.unread, 0) });
      if (!collapsedFolders.includes(f.name)) addLevel(f.name, depth + 1);
    }
  };
  addLevel(null, 0);
  const toggleFolder = (name: string) =>
    setLocal({ collapsedFolders: collapsedFolders.includes(name) ? collapsedFolders.filter((f) => f !== name) : [...collapsedFolders, name] });
  /**
   * After a rename or move: follow the open folder, keep collapsed subfolders collapsed under their new path, and
   * open the folder it landed in so it stays in sight.
   */
  const folderRenamed = (from: string, to: string) => {
    const rebase = (p: string) => (isSelfOrInside(p, from) ? to + p.slice(from.length) : p);
    if (view.kind === 'mail' && view.folder) setView({ ...view, folder: rebase(view.folder) });
    setLocal({ collapsedFolders: collapsedFolders.map(rebase).filter((f) => f !== folderParent(to)) });
    refreshMail();
  };
  const moveTo = async (from: string, parent: string | null) => {
    const to = await moveFolder(from, parent ? `${parent}/${folderLeaf(from)}` : folderLeaf(from));
    if (to) folderRenamed(from, to);
  };
  /** After a folder is merged away or deleted, leave it if it was open. */
  const folderGone = (name: string, goTo = 'Inbox') => {
    if (view.kind === 'mail' && view.folder && isSelfOrInside(view.folder, name)) setView({ kind: 'mail', folder: goTo });
    refreshMail();
  };

  return (
    <motion.aside
      animate={{ width: collapsed ? 68 : 248 }}
      transition={{ type: 'spring', stiffness: 400, damping: 40 }}
      className="relative z-10 flex h-full shrink-0 flex-col border-r border-line"
    >
      <div className={cn('drag flex h-[52px] shrink-0 items-center gap-2.5', isMac ? 'pl-[84px]' : 'pl-4', collapsed && 'justify-center pl-0')}>
        {!(isMac && collapsed) && (
          <img src={dtsMark} alt="DTS" className="h-6 w-auto drop-shadow-[0_0_8px_rgba(0,212,255,0.35)]" draggable={false} />
        )}
        {!collapsed && (
          <div className="leading-tight">
            <div className="text-[14px] font-semibold tracking-tight">DTS Mail</div>
            <LiveDot />
          </div>
        )}
      </div>

      <div className={cn('flex gap-2 px-3 pb-3', collapsed && 'flex-col items-center px-2')}>
        <button
          onClick={() => composeNew()}
          title="New message (C)"
          className={cn('btn-legend group flex h-10 flex-1 items-center justify-center gap-2 rounded-xl bg-fg text-[13.5px] font-semibold text-bg shadow-lg transition hover:opacity-90 active:scale-[0.97]', collapsed && 'size-10 flex-none')}
        >
          <PenSquare className="size-4" />
          {!collapsed && 'Compose'}
        </button>
        <button
          onClick={() => composeWithAi()}
          title="Compose with AI (Ctrl+Shift+N)"
          className="ai-gradient flex size-10 items-center justify-center rounded-xl text-white shadow-lg glow transition hover:brightness-110 active:scale-95"
        >
          <Sparkles className="size-4" />
        </button>
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 pb-3">
        <NavItem collapsed={collapsed} icon={<Sun />} label="Today" target={{ kind: 'today' }} accent />
        <NavItem collapsed={collapsed} icon={ICONS.Inbox} label="Inbox" count={inboxUnread} target={{ kind: 'mail', folder: 'Inbox' }} onDropIds={move('Inbox')} />
        <NavItem collapsed={collapsed} icon={<Mail />} label="UnRead" count={unreadEverywhere} depth={1} title="Unread mail in every folder except Junk and Trash" target={{ kind: 'mail', filter: 'unread' }} />
        <NavItem collapsed={collapsed} icon={<Sparkles />} label="Needs reply" target={{ kind: 'mail', folder: 'Inbox', filter: 'needs_reply' }} accent />
        <NavItem collapsed={collapsed} icon={<Flag />} label="Flagged" target={{ kind: 'mail', filter: 'flagged' }} />
        {(['Sent', 'Drafts', 'Archive', 'Junk', 'Trash'] as const).map((f) => (
          <NavItem
            key={f}
            collapsed={collapsed}
            icon={ICONS[f]}
            label={f}
            count={f === 'Drafts' ? draftCount : f === 'Junk' ? folders.find((x) => x.name === 'Junk')?.unread : undefined}
            target={{ kind: 'mail', folder: f }}
            onDropIds={f === 'Drafts' || f === 'Sent' ? undefined : move(f)}
          />
        ))}

        {!collapsed && (
          <div
            title="Drop an email here to make a folder for its sender's domain, or a folder to move it to the top level"
            onDragOver={(e) => {
              const types = e.dataTransfer.types;
              if (!types.includes('application/x-dts-from') && !types.includes(FOLDER_DRAG)) return;
              e.preventDefault();
              setOverFolders(true);
            }}
            onDragLeave={() => setOverFolders(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOverFolders(false);
              const folder = e.dataTransfer.getData(FOLDER_DRAG);
              if (folder) {
                if (folderParent(folder)) void moveTo(folder, null);
                return;
              }
              const from = e.dataTransfer.getData('application/x-dts-from');
              if (from) void fileDomain(from);
            }}
            className={cn('mt-4 flex items-center justify-between rounded-xl px-2.5 pb-1 pt-1 transition', overFolders && 'bg-accent/15 ring-1 ring-accent/50')}
          >
            <span className="legend text-[11px] font-semibold uppercase tracking-[0.08em] text-faint">{overFolders ? 'Drop to file by domain' : 'Folders'}</span>
            <IconButton label="New folder" size="sm" onClick={() => void newFolder()}>
              <FolderPlus />
            </IconButton>
          </div>
        )}
        {tree.map(({ folder: f, depth, hasChildren, unreadInside }) => (
          <NavItem
            key={f.name}
            collapsed={collapsed}
            icon={<FolderIcon name={f.name} />}
            label={folderLeaf(f.name)}
            title={depth ? f.name : undefined}
            depth={depth}
            expanded={hasChildren ? !collapsedFolders.includes(f.name) : undefined}
            onToggle={hasChildren ? () => toggleFolder(f.name) : undefined}
            // A collapsed parent also counts its hidden subfolders' unread mail.
            count={f.unread + (hasChildren && collapsedFolders.includes(f.name) ? unreadInside : 0)}
            target={{ kind: 'mail', folder: f.name }}
            onDropIds={move(f.name)}
            folderDrag={f.name}
            // Dropping one folder on another asks whether to nest it or merge the two.
            onDropFolder={(source, at) => {
              const from = folderByName(source);
              if (!from || isSelfOrInside(f.name, source)) return;
              setMenu({
                ...at,
                items: [
                  { icon: <FolderTree />, label: `Move inside ${folderLeaf(f.name)}`, run: () => void moveTo(source, f.name) },
                  { icon: <FolderInput />, label: `Merge into ${folderLeaf(f.name)}`, run: () => setMerging({ folder: from, target: f.name }) },
                ],
              });
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              setMenu({
                x: e.clientX,
                y: e.clientY,
                items: folderMenuItems({
                  onNewSubfolder: () => void newFolder(f.name),
                  onRename: () =>
                    void renameFolder(f.name, (title, initial) => askText(title, { initial, confirmLabel: 'Rename' })).then((to) => to && folderRenamed(f.name, to)),
                  onMove: () => setMoving(f),
                  onMerge: () => setMerging({ folder: f }),
                  onDelete: () => setDeleting(f),
                }),
              });
            }}
          />
        ))}

        {identities.length > 0 && !collapsed && (
          <div className="legend px-2.5 pb-1 pt-5 text-[11px] font-semibold uppercase tracking-[0.08em] text-faint">
            {domains.length > 1 ? 'Domains' : 'Domain'}
          </div>
        )}
        {domains.map((d) => (
          <NavItem
            key={d}
            collapsed={collapsed}
            icon={<Favicon domain={d} size={17} />}
            label={d}
            target={{ kind: 'mail', folder: 'Inbox', domain: d }}
          />
        ))}
      </nav>

      <div className={cn('space-y-0.5 border-t border-line p-3', collapsed && 'px-2')}>
        <NavItem collapsed={collapsed} icon={<Users />} label="Contacts" target={{ kind: 'contacts' }} />
        <button
          onClick={() => setCrmPanel(true)}
          title="CRM activity"
          className={cn('nav-item flex h-9 w-full items-center gap-3 rounded-xl px-2.5 text-[13.5px] font-medium text-muted transition hover:text-fg', collapsed && 'justify-center px-0')}
        >
          <Workflow className={cn('size-[17px]', crmRunning && 'animate-pulse text-accent')} />
          {!collapsed && <span className="flex-1 text-left">CRM activity</span>}
          {crmRunning && !collapsed && <span className="size-2 rounded-full bg-accent shadow-[0_0_10px] shadow-accent" />}
        </button>
        <NavItem collapsed={collapsed} icon={<Settings />} label="Settings" target={{ kind: 'settings' }} />
        <div className={cn('flex items-center gap-2.5 pt-2', collapsed && 'flex-col')}>
          {me && <Avatar name={me.user.displayName} address={me.user.email} size={30} />}
          {!collapsed && me && (
            <div className="min-w-0 flex-1 leading-tight">
              <div className="truncate text-[13px] font-medium">{me.user.displayName}</div>
              <div className="truncate text-[11.5px] text-faint">{me.user.email}</div>
            </div>
          )}
          <IconButton label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} size="sm" onClick={() => setLocal({ sidebarCollapsed: !collapsed })}>
            {collapsed ? <ChevronsRight /> : <ChevronsLeft />}
          </IconButton>
        </div>
      </div>
      <PoweredBy compact={collapsed} className="flex w-full shrink-0 pb-3" />

      {menu && (
        <FloatingMenu at={menu} items={menu.items} onClose={() => setMenu(null)} />
      )}
      <MoveFolderDialog
        source={moving}
        folders={folders}
        onClose={() => setMoving(null)}
        onDone={(from, to) => {
          setMoving(null);
          folderRenamed(from, to);
        }}
      />
      <MergeFolderDialog
        source={merging?.folder ?? null}
        initialTarget={merging?.target}
        folders={folders}
        onClose={() => setMerging(null)}
        onDone={(target) => {
          if (merging) folderGone(merging.folder.name, target);
          setMerging(null);
        }}
      />
      <DeleteFolderDialog
        folder={deleting}
        folders={folders}
        onClose={() => setDeleting(null)}
        onDone={() => {
          if (deleting) folderGone(deleting.name);
          setDeleting(null);
        }}
      />
    </motion.aside>
  );
}

function LiveDot() {
  const [state, setState] = useState<'connected' | 'error' | 'stopped'>('stopped');
  useEffect(() => {
    void window.dts.events.status().then((s) => setState(s.state));
    return window.dts.events.onStatus((s) => setState(s));
  }, []);
  return (
    <div className="flex items-center gap-1 text-[10.5px] text-faint" title="Live updates via your event queue">
      <span className={cn('size-1.5 rounded-full', state === 'connected' ? 'bg-ok' : state === 'error' ? 'bg-warn' : 'bg-faint')} />
      {state === 'connected' ? 'Live' : state === 'error' ? 'Reconnecting' : 'Offline'}
    </div>
  );
}
